import { Box, Button, Checkbox, Flex, Input, Select } from '@chakra-ui/react';
import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';

import { ImportCsvDelimiter, Task } from '@app/api/tasks';
import { Dialog, Txt } from '@app/components/common';
import { useAlert, useApi } from '@app/hooks';

import { TASK_TYPE_LABELS } from '../constants';

type ImportKind = 'sku.import' | 'outlet.routes';

const DELIM_OPTIONS: { id: ImportCsvDelimiter; name: string }[] = [
  { id: 'auto', name: 'Авто' },
  { id: 'semicolon', name: 'Точка с запятой (;)' },
  { id: 'comma', name: 'Запятая (,)' },
  { id: 'tab', name: 'Табуляция' },
];

const KIND_OPTIONS: { id: ImportKind; name: string }[] = [
  { id: 'sku.import', name: TASK_TYPE_LABELS['sku.import'] },
  { id: 'outlet.routes', name: TASK_TYPE_LABELS['outlet.routes'] },
];

type CreateImportTaskDialogProps = {
  open: boolean;
  onClose: () => void;
  onCreated?: (taskId: number) => void;
};

/** Создание фоновой задачи импорта: CSV → sku.import или outlet.routes. */
export const CreateImportTaskDialog = ({ open, onClose, onCreated }: CreateImportTaskDialogProps) => {
  const api = useApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  const { success, error: alertError, warning } = useAlert();

  const [kind, setKind] = useState<ImportKind>('sku.import');
  const [file, setFile] = useState<File | null>(null);
  const [dryRun, setDryRun] = useState(false);
  const [exact, setExact] = useState(false);
  const [forceUpdate, setForceUpdate] = useState(false);
  const [deactivateExisting, setDeactivateExisting] = useState(true);
  const [header, setHeader] = useState(true);
  const [delim, setDelim] = useState<ImportCsvDelimiter>('auto');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }
    setKind('sku.import');
    setFile(null);
    setDryRun(false);
    setExact(false);
    setForceUpdate(false);
    setDeactivateExisting(true);
    setHeader(true);
    setDelim('auto');
    setSubmitting(false);
  }, [open]);

  const onFileChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setFile(event.target.files?.[0] || null);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!file) {
      alertError('Выберите CSV-файл');
      return;
    }

    setSubmitting(true);
    try {
      const response =
        kind === 'outlet.routes'
          ? await apiRef.current.tasks.importOutletRoutes({
              file,
              dry_run: dryRun,
              deactivate_existing: deactivateExisting,
              header,
              delim,
            })
          : await apiRef.current.tasks.importSku({
              file,
              dry_run: dryRun,
              exact,
              force_update: forceUpdate,
              header,
              delim,
            });

      // Ошибку с code !== 200 useApi уже показал тостом.
      if (response?.code !== 200 || !response.data) {
        return;
      }

      const task: Task = response.data;
      const skipped = task.skipped ?? 0;
      const typeLabel = TASK_TYPE_LABELS[kind] || kind;
      if (skipped > 0) {
        const skipHint = kind === 'outlet.routes' ? 'без ID точки или названия маршрута' : 'без кода SKU';
        warning(
          `Задача #${task.id} (${typeLabel}) создана. Пропущено строк ${skipHint}: ${skipped}` +
            (task.warnings?.length ? `. ${task.warnings.slice(0, 3).join('; ')}` : '')
        );
      } else {
        success(`Задача #${task.id} (${typeLabel}) поставлена в очередь`);
      }

      onClose();
      if (task.id != null) {
        onCreated?.(task.id);
      }
    } catch (e) {
      console.error(e);
      alertError((e as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Не удалось создать задачу');
    } finally {
      setSubmitting(false);
    }
  }, [
    file,
    kind,
    dryRun,
    exact,
    forceUpdate,
    deactivateExisting,
    header,
    delim,
    alertError,
    success,
    warning,
    onClose,
    onCreated,
  ]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Создание фоновой задачи"
      width="560px"
      loading={submitting}
      actions={
        <Flex gap={2} justifyContent="flex-end">
          <Button colorScheme="brand" onClick={handleSubmit} isLoading={submitting} isDisabled={!file}>
            Создать задачу
          </Button>
          <Button onClick={onClose} isDisabled={submitting}>
            Отмена
          </Button>
        </Flex>
      }
    >
      <Flex direction="column" gap={4}>
        <Box>
          <Txt color="gray.400" fontSize="sm" mb={1}>
            Тип задачи
          </Txt>
          <Select size="sm" value={kind} onChange={(event) => setKind(event.target.value as ImportKind)}>
            {KIND_OPTIONS.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </Select>
        </Box>

        {kind === 'sku.import' ? (
          <Box>
            <Txt color="gray.500" fontSize="sm" mb={2}>
              Файл разбирается на шлюзе и ставится задача sku.import. Прогресс смотрите в карточке задачи.
            </Txt>
            <Txt color="gray.500" fontSize="sm" mb={1}>
              Колонки CSV:
            </Txt>
            <Box as="ul" pl={4} color="gray.500" fontSize="sm" sx={{ listStyleType: 'disc' }}>
              <Box as="li">1 — код SKU (по нему ищем SKU)</Box>
              <Box as="li">2–3 — группа и подгруппа (если заполнены — пробуем добавить и привязать к SKU)</Box>
              <Box as="li">4 — ссылка на фото SKU</Box>
              <Box as="li">5 — number_in_package</Box>
              <Box as="li">6 — ID дистрибьютора</Box>
              <Box as="li">
                7–8 — название и код в системе дистрибьютора (если заполнены — добавляются связи с SKU
                дистрибьютора)
              </Box>
            </Box>
          </Box>
        ) : (
          <Box>
            <Txt color="gray.500" fontSize="sm" mb={2}>
              Файл разбирается на шлюзе и ставится задача outlet.routes. Прогресс смотрите в карточке задачи.
            </Txt>
            <Txt color="gray.500" fontSize="sm" mb={1}>
              Колонки CSV:
            </Txt>
            <Box as="ul" pl={4} color="gray.500" fontSize="sm" sx={{ listStyleType: 'disc' }}>
              <Box as="li">1 — ID торговой точки</Box>
              <Box as="li">2 — название маршрута (точное совпадение)</Box>
              <Box as="li">3 — дни посещения или «не посещается» (пустая ячейка — без дней посещения)</Box>
            </Box>
          </Box>
        )}

        <Box>
          <Txt color="gray.400" fontSize="sm" mb={1}>
            CSV-файл
          </Txt>
          <Input type="file" accept=".csv,text/csv" onChange={onFileChange} p={1} height="auto" />
          {file && (
            <Txt color="gray.500" fontSize="sm" mt={1}>
              {file.name} · {(file.size / 1024).toFixed(1)} КБ
            </Txt>
          )}
        </Box>

        <Box>
          <Txt color="gray.400" fontSize="sm" mb={1}>
            Разделитель
          </Txt>
          <Select size="sm" value={delim} onChange={(event) => setDelim(event.target.value as ImportCsvDelimiter)}>
            {DELIM_OPTIONS.map((option) => (
              <option key={option.id || 'auto'} value={option.id}>
                {option.name}
              </option>
            ))}
          </Select>
        </Box>

        <Flex direction="column" gap={2}>
          <Checkbox isChecked={header} onChange={(event) => setHeader(event.target.checked)}>
            Первая строка — заголовок
          </Checkbox>
          <Checkbox isChecked={dryRun} onChange={(event) => setDryRun(event.target.checked)}>
            Только отчёт (dry run), без записи в SNEG
          </Checkbox>
          {kind === 'sku.import' ? (
            <>
              <Checkbox isChecked={exact} onChange={(event) => setExact(event.target.checked)}>
                Точное совпадение кода SKU
              </Checkbox>
              <Checkbox isChecked={forceUpdate} onChange={(event) => setForceUpdate(event.target.checked)}>
                Принудительно обновлять number_in_package
              </Checkbox>
            </>
          ) : (
            <Checkbox
              isChecked={deactivateExisting}
              onChange={(event) => setDeactivateExisting(event.target.checked)}
            >
              Деактивировать текущие привязки ТТ к маршрутам перед созданием новой
            </Checkbox>
          )}
        </Flex>
      </Flex>
    </Dialog>
  );
};
